import type { JSX, MouseEvent } from 'react';
import { useCallback, useState } from 'react';
import { FileCode2, Trash2 } from 'lucide-react';

import { DocumentViewerDialog } from '~/components/DocumentViewerDialog';
import { Tooltip } from '~/components/Tooltip';

interface ProjectCardActionsProps {
  projectId: string;
  projectTitle: string;
  onClickDelete: () => void;
  stopPropagation: (e: MouseEvent) => void;
}

/**
 * Hover-revealed action buttons for ProjectCard (view spec, delete).
 */
export function ProjectCardActions(props: ProjectCardActionsProps): JSX.Element | null {
  const { onClickDelete, projectId, projectTitle, stopPropagation } = props;

  const [showTechSpecDialog, setShowTechSpecDialog] = useState(false);

  const handleOpenTechSpec = useCallback(
    (e: MouseEvent) => {
      stopPropagation(e);
      setShowTechSpecDialog(true);
    },
    [stopPropagation],
  );

  const handleOpenDelete = useCallback(
    (e: MouseEvent) => {
      stopPropagation(e);
      onClickDelete();
    },
    [stopPropagation, onClickDelete],
  );

  return (
    <>
      {/* Hover-revealed action buttons */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={stopPropagation} className="flex items-center gap-1 shrink-0">
        <Tooltip content="View project spec">
          <button
            type="button"
            aria-label="View project spec"
            onClick={handleOpenTechSpec}
            className="inline-flex items-center justify-center p-1 rounded text-foreground-500 hover:text-foreground hover:bg-foreground-100 transition-colors"
          >
            <FileCode2 className="w-3.5 h-3.5" />
          </button>
        </Tooltip>
        <Tooltip content="Delete project">
          <button
            type="button"
            aria-label="Delete project"
            onClick={handleOpenDelete}
            className="inline-flex items-center justify-center p-1 rounded text-foreground-500 hover:text-danger hover:bg-foreground-100 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </Tooltip>
      </div>

      {/* Project Spec Viewer Dialog */}
      <DocumentViewerDialog
        documentType="technical-spec"
        isOpen={showTechSpecDialog}
        onClose={() => setShowTechSpecDialog(false)}
        projectId={projectId}
        title={`Spec: ${projectTitle}`}
      />
    </>
  );
}
